import { useState, useEffect, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

export function useDashboardCustomization(storageKey, defaultWidgets, defaultOrder, defaultSizes, isAllowed) {
  const [isCustomizing, setIsCustomizing] = useState(false);

  const [visibility, setVisibility] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${storageKey}_widgets`)) || defaultWidgets; }
    catch { return defaultWidgets; }
  });
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${storageKey}_order`)) || defaultOrder; }
    catch { return defaultOrder; }
  });
  const [widgetSizes, setWidgetSizes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`${storageKey}_sizes`)) || defaultSizes; }
    catch { return defaultSizes; }
  });

  // Ensure all keys are in order & prune stale keys
  useEffect(() => {
    const allKeys = Object.keys(defaultWidgets);
    const missing = allKeys.filter(k => !widgetOrder.includes(k));
    const stale = widgetOrder.filter(k => !allKeys.includes(k));
    if (missing.length > 0 || stale.length > 0) {
      const next = widgetOrder.filter(k => allKeys.includes(k)).concat(missing);
      setWidgetOrder(next);
      localStorage.setItem(`${storageKey}_order`, JSON.stringify(next));
    }
    // Also prune stale visibility entries
    const visCleaned = { ...defaultWidgets };
    for (const k of allKeys) {
      if (visibility[k] !== undefined) visCleaned[k] = visibility[k];
    }
    const hasStaleVis = Object.keys(visibility).some(k => !allKeys.includes(k));
    if (hasStaleVis) {
      setVisibility(visCleaned);
      localStorage.setItem(`${storageKey}_widgets`, JSON.stringify(visCleaned));
    }
  }, [widgetOrder, defaultWidgets, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleWidget = (key) => {
    const next = { ...visibility, [key]: !visibility[key] };
    setVisibility(next);
    localStorage.setItem(`${storageKey}_widgets`, JSON.stringify(next));
  };

  const NEXT_SIZE = { small: 'large', large: 'full', full: 'small' };
  const resizeWidget = (key) => {
    const next = { ...widgetSizes, [key]: NEXT_SIZE[widgetSizes[key] || 'full'] };
    setWidgetSizes(next);
    localStorage.setItem(`${storageKey}_sizes`, JSON.stringify(next));
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWidgetOrder(prev => {
      const oldIdx = prev.indexOf(active.id);
      const newIdx = prev.indexOf(over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      localStorage.setItem(`${storageKey}_order`, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const allowed = (k) => (isAllowed ? isAllowed(k) : true);
  const visibleWidgets = widgetOrder.filter(k => visibility[k] && allowed(k));

  return {
    isCustomizing, setIsCustomizing,
    visibility, widgetSizes, visibleWidgets,
    toggleWidget, resizeWidget, handleDragEnd,
  };
}
